// MIGRATED FROM: Solar API only
// MIGRATION DATE: 2024-08-24
// CHANGE TYPE: ENHANCED - Integrated multiple api-client Service-Offer clients
// BACKWARD COMPATIBLE: Yes - All existing Solar API methods preserved

import { AxiosInstance } from 'axios'
import { UserApiServiceOffer, UserApiRelatedServiceOffer, UserApiServiceOfferInHospital } from 'api-client'

import { ServiceOfferListResponse } from '../service-offer.type'

import { buildApiEndpoint, createSolarApiClient } from '@/shared/libs/api-client'

import type {
  GetServiceOfferListPayload,
  GetServiceOfferPayload,
  GetServiceOfferLogPayload,
  GetServiceOfferLogDto,
  ServiceOfferListRequestParams,
  UserServiceOfferDetails,
  UserServiceOfferOptions,
  UserServiceOfferLogDetails,
  UserRelatedServiceOffer,
  UserServiceOfferInHospitalFilteredListView,
  UserServiceOfferInHospitalCategories,
  UserServiceOfferInHospitalTreatmentResponse,
  UserServiceOfferInHospitalFilteredListRequestParams,
  UserServiceOfferInHospitalTreatmentRequestParams,
  ServiceOffer,
} from '@/features/service-offer/service-offer.type'

export class ServiceOfferService {
  // API-client integration via composition
  private readonly userApiServiceOffer: UserApiServiceOffer
  private readonly userApiRelatedServiceOffer: UserApiRelatedServiceOffer
  private readonly userApiServiceOfferInHospital: UserApiServiceOfferInHospital

  constructor(private readonly apiClient: AxiosInstance) {
    // Initialize API-client instances
    this.userApiServiceOffer = new UserApiServiceOffer(apiClient)
    this.userApiRelatedServiceOffer = new UserApiRelatedServiceOffer(apiClient)
    this.userApiServiceOfferInHospital = new UserApiServiceOfferInHospital(apiClient)
  }

  async getServiceOfferList({
    filters,
    page = 1,
    pageSize = 10,
  }: GetServiceOfferListPayload): Promise<ServiceOfferListResponse> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/service-offer/query/user/service-offer-list/process',
        data: { filters, page, pageSize },
      },
      'solar',
    )
    const res = await this.apiClient.request<ServiceOfferListResponse>(config)
    return res.data
  }

  async getServiceOffer({ serviceOfferId }: GetServiceOfferPayload): Promise<ServiceOffer> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/service-offer/query/user/service-offer-detail/process',
        data: { id: serviceOfferId },
      },
      'solar',
    )
    const res = await this.apiClient.request<ServiceOffer>(config)
    return res.data
  }

  async getServiceOfferLog({ serviceOfferId }: GetServiceOfferLogPayload): Promise<GetServiceOfferLogDto> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/service-offer/query/user/service-offer-log/process',
        data: { id: serviceOfferId },
      },
      'solar',
    )
    const res = await this.apiClient.request<GetServiceOfferLogDto>(config)
    return res.data
  }

  /**
   * @documentation https://service-offer-api.test.gnst.me/swagger-ui/index.html#/user-service-offers-controller/getFilterListViews
   * @param headers
   * @param params
   * @returns
   */
  async getServiceOfferListWithFilters({
    filters,
    pagination,
  }: ServiceOfferListRequestParams): Promise<ServiceOfferListResponse> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/service-offer/query/user/filter-list-views/process',
        data: { filters, pagination },
      },
      'solar',
    )
    const res = await this.apiClient.request<ServiceOfferListResponse>(config)
    return res.data
  }

  /**
   * Get related service offers (events) based on tag information
   */
  async getRelatedServiceOffers({
    tagType,
    tagIdList,
    hospitalCountry = 'JP',
    country,
    hospitalAssessmentState,
    hospitalIdList,
    _language,
  }: {
    tagType: string
    tagIdList: number[]
    hospitalCountry?: string
    country?: string
    hospitalAssessmentState?: string
    hospitalIdList?: number[]
    _language: string
  }): Promise<ServiceOffer[]> {
    if (!tagType || !tagIdList) {
      return []
    }

    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/service-offer/query/user/recommended-list/process',
        data: {
          type: tagType,
          tagIdList: tagIdList.join(','),
          size: 10,
          hospitalCountry,
          hospitalIdList: hospitalIdList?.join(','),
          hospitalAssessmentState,
          country,
        },
      },
      'solar',
    )

    try {
      const res = await this.apiClient.request<{ items: ServiceOffer[] }>(config)
      return res.data.items
    } catch (error) {
      console.warn('related service offers api call', error)
      return []
    }
  }

  // ========================
  // API-CLIENT INTEGRATION - New methods using api-client
  // ========================

  // Enhanced service offer operations via API-client
  /**
   * @method UserApiServiceOffer.getById
   * @path POST /service-offer/query/user/service-offer-detail/process
   * @returns Promise<UserServiceOfferDetails>
   */
  getServiceOfferFromApi = async (id: number): Promise<UserServiceOfferDetails> => {
    return this.userApiServiceOffer.getById(id)
  }

  /**
   * @method UserApiServiceOffer.getOptions
   * @path POST /service-offer/query/user/options/process
   * @returns Promise<UserServiceOfferOptions>
   */
  getServiceOfferOptionsFromApi = async (serviceOfferId: number): Promise<UserServiceOfferOptions> => {
    return this.userApiServiceOffer.getOptions(serviceOfferId)
  }

  /**
   * @method UserApiServiceOffer.getLogDetails
   * @path POST /service-offer/log/query/service-offer-detail/process
   * @returns Promise<UserServiceOfferLogDetails>
   */
  getServiceOfferLogDetailsFromApi = async (id: number): Promise<UserServiceOfferLogDetails> => {
    return this.userApiServiceOffer.getLogDetails(id)
  }

  // User tracking via API-client
  /**
   * @method UserApiServiceOffer.trackViewed
   * @path POST /command/user/service-offer-detail-viewed/execute
   * @returns Promise<void>
   */
  trackServiceOfferViewedFromApi = async (id: number): Promise<void> => {
    return this.userApiServiceOffer.trackViewed(id)
  }

  /**
   * @method UserApiServiceOffer.postUserLog
   * @path POST /v1/user-log/event
   * @returns Promise<void>
   */
  postServiceOfferUserLogFromApi = async (
    log: Parameters<typeof this.userApiServiceOffer.postUserLog>[0],
  ): Promise<void> => {
    return this.userApiServiceOffer.postUserLog(log)
  }

  // Related service offers via API-client
  /**
   * @method UserApiRelatedServiceOffer.getList
   * @path POST /service-offer/query/user/related-list/process
   * @returns Promise<UserRelatedServiceOffer[]>
   */
  getRelatedServiceOffersFromApi = async (serviceOfferId: number): Promise<UserRelatedServiceOffer[]> => {
    return this.userApiRelatedServiceOffer.getList(serviceOfferId)
  }

  // Hospital service offers via API-client
  /**
   * @method UserApiServiceOfferInHospital.getFilteredList
   * @path POST /service-offer/query/user/filter-list-views/process
   * @returns Promise<UserServiceOfferInHospitalFilteredListView>
   */
  getServiceOfferFilteredListFromApi = async (
    params: UserServiceOfferInHospitalFilteredListRequestParams,
  ): Promise<UserServiceOfferInHospitalFilteredListView> => {
    return this.userApiServiceOfferInHospital.getFilteredList(params)
  }

  /**
   * @method UserApiServiceOfferInHospital.getTreatmentCategory
   * @path POST /service-offer/query/user/treatment-category-count/process
   * @returns Promise<UserServiceOfferInHospitalCategories>
   */
  getTreatmentCategoryFromApi = async (hospitalId: number): Promise<UserServiceOfferInHospitalCategories> => {
    return this.userApiServiceOfferInHospital.getTreatmentCategory(hospitalId)
  }

  /**
   * @method UserApiServiceOfferInHospital.getTreatment
   * @path POST /service-offer/query/user/treatment-count/process
   * @returns Promise<UserServiceOfferInHospitalTreatmentResponse>
   */
  getTreatmentFromApi = async (
    params: UserServiceOfferInHospitalTreatmentRequestParams,
  ): Promise<UserServiceOfferInHospitalTreatmentResponse> => {
    return this.userApiServiceOfferInHospital.getTreatment(params)
  }
}

export const clientServiceOfferService = new ServiceOfferService(createSolarApiClient())
